import { z } from "zod";

/**
 * Zod schema for the Remote Desktop portal target.
 *
 * - `full_desktop` → the Windows 365 device gallery (https://windows.cloud.microsoft/#/devices)
 * - `apps`         → the Windows 365 app gallery   (https://windows.cloud.microsoft/#/apps)
 */
export const remoteDesktopPortalTargetSchema = z.enum(["full_desktop", "apps"]);

export type RemoteDesktopPortalTarget = z.infer<
  typeof remoteDesktopPortalTargetSchema
>;

/**
 * Canonical portal URLs for each RemoteDesktopPortalTarget.
 * Only use these when the caller explicitly chose a target; preserve the
 * configured avdUrl when inferring the target so custom/government portals
 * are not silently replaced.
 */
export const REMOTE_DESKTOP_PORTAL_URLS: Record<
  RemoteDesktopPortalTarget,
  string
> = {
  full_desktop: "https://windows.cloud.microsoft/#/devices",
  apps: "https://windows.cloud.microsoft/#/apps",
};

/**
 * Returns a human-readable label for a given portal target.
 */
export function formatRemoteDesktopPortalTarget(
  target: RemoteDesktopPortalTarget,
): string {
  return target === "full_desktop" ? "full desktop" : "apps";
}

/**
 * Infers the portal target from a configured avdUrl.
 * Defaults to `full_desktop` for any URL that does not contain `#/apps`.
 */
export function inferRemoteDesktopPortalTarget(
  avdUrl: string,
): RemoteDesktopPortalTarget {
  return avdUrl.trim().toLowerCase().includes("#/apps")
    ? "apps"
    : "full_desktop";
}
